import React, { useState, useEffect } from 'react';
import { getRuote, getEstrazioni, getEstrazioniPerData, getEstrazioniPerNumero } from '../services/api';

const Estrazioni = () => {
  const [ruote, setRuote] = useState([]);
  const [estrazioni, setEstrazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('recenti');
  
  // Filtri
  const [selectedRuota, setSelectedRuota] = useState('');
  const [dataSelezionata, setDataSelezionata] = useState('');
  const [numeroRicerca, setNumeroRicerca] = useState('');
  
  // Paginazione
  const [offset, setOffset] = useState(0);
  const limit = 110; // 10 date * 11 ruote

  useEffect(() => {
    loadRuote();
  }, []);

  useEffect(() => {
    if (activeTab === 'recenti') {
      loadEstrazioniRecenti();
    }
  }, [activeTab, selectedRuota, offset]);

  const loadRuote = async () => {
    try {
      const res = await getRuote();
      setRuote(res.data);
    } catch (err) {
      console.error('Errore caricamento ruote:', err);
    }
  };

  const loadEstrazioniRecenti = async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (selectedRuota) params.ruota_id = selectedRuota;
      
      const res = await getEstrazioni(params);
      setEstrazioni(res.data);
    } catch (err) {
      console.error('Errore:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCercaPerData = async () => {
    if (!dataSelezionata) return;
    
    setLoading(true);
    setActiveTab('data');
    try {
      const res = await getEstrazioniPerData(dataSelezionata);
      setEstrazioni(res.data);
    } catch (err) {
      console.error('Errore:', err);
      setEstrazioni([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCercaPerNumero = async () => {
    const num = parseInt(numeroRicerca);
    if (!num || num < 1 || num > 90) {
      alert('Inserisci un numero valido (1-90)');
      return;
    }
    
    setLoading(true);
    setActiveTab('numero');
    try {
      const res = await getEstrazioniPerNumero(num, selectedRuota || null, 100);
      setEstrazioni(res.data);
    } catch (err) {
      console.error('Errore:', err);
      setEstrazioni([]);
    } finally {
      setLoading(false);
    }
  };

  const raggruppaPerData = (estrazioni) => {
    const grouped = {};
    estrazioni.forEach(e => {
      const data = e.data;
      if (!grouped[data]) grouped[data] = [];
      grouped[data].push(e);
    });
    return grouped;
  };

  const estrazioniRaggruppate = raggruppaPerData(estrazioni);
  const dateOrdinate = Object.keys(estrazioniRaggruppate).sort((a, b) => new Date(b) - new Date(a));

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>📅 Archivio Estrazioni</h2>

      {/* Filtri */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="grid grid-3" style={{ gap: '20px', alignItems: 'flex-end' }}>
          {/* Cerca per data */}
          <div>
            <label className="form-label">Cerca per data</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="date"
                className="form-input"
                value={dataSelezionata}
                onChange={(e) => setDataSelezionata(e.target.value)}
              />
              <button className="btn btn-primary" onClick={handleCercaPerData}>
                🔍
              </button>
            </div>
          </div>

          {/* Cerca per numero */}
          <div>
            <label className="form-label">Cerca per numero</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="number"
                className="form-input"
                min="1"
                max="90"
                value={numeroRicerca}
                onChange={(e) => setNumeroRicerca(e.target.value)}
                placeholder="1-90"
              />
              <button className="btn btn-primary" onClick={handleCercaPerNumero}>
                🔍
              </button>
            </div>
          </div>

          {/* Filtro ruota */}
          <div>
            <label className="form-label">Filtra per ruota</label>
            <select
              className="form-select"
              value={selectedRuota}
              onChange={(e) => { setSelectedRuota(e.target.value); setOffset(0); }}
            >
              <option value="">Tutte le ruote</option>
              {ruote.map((r) => (
                <option key={r.id} value={r.id}>{r.nome}</option>
              ))}
            </select>
          </div>
        </div>

        {activeTab !== 'recenti' && (
          <div style={{ marginTop: '15px' }}>
            <button 
              className="btn btn-secondary"
              onClick={() => { setActiveTab('recenti'); setOffset(0); }}
            >
              ← Torna alle estrazioni recenti
            </button>
          </div>
        )}
      </div>

      {/* Risultati */}
      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <>
          {/* Intestazione risultati */}
          <div style={{ marginBottom: '15px', color: '#718096' }}>
            {activeTab === 'recenti' && `Ultime estrazioni`}
            {activeTab === 'data' && `Estrazione del ${new Date(dataSelezionata).toLocaleDateString('it-IT')}`}
            {activeTab === 'numero' && `Estrazioni con il numero ${numeroRicerca}`}
            {' '} - {estrazioni.length} risultati
          </div>

          {estrazioni.length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', color: '#718096', padding: '20px' }}>
                Nessuna estrazione trovata
              </p>
            </div>
          ) : (
            <>
              {dateOrdinate.map((data) => (
                <div key={data} className="card" style={{ marginBottom: '15px' }}>
                  <h3 style={{ 
                    marginBottom: '15px', 
                    paddingBottom: '10px', 
                    borderBottom: '2px solid #e2e8f0',
                    color: '#1a365d'
                  }}>
                    📅 {new Date(data).toLocaleDateString('it-IT', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
                    {estrazioniRaggruppate[data]
                      .sort((a, b) => a.ruota_nome.localeCompare(b.ruota_nome))
                      .map((e) => (
                        <div 
                          key={e.id} 
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px',
                            padding: '8px 12px',
                            background: '#f7fafc',
                            borderRadius: '8px'
                          }}
                        >
                          <span 
                            className="ruota-badge" 
                            style={{ 
                              minWidth: '80px', 
                              textAlign: 'center',
                              background: '#2c5282',
                              color: 'white'
                            }}
                          >
                            {e.ruota_nome}
                          </span>
                          <div style={{ display: 'flex', gap: '5px' }}>
                            {[e.numero1, e.numero2, e.numero3, e.numero4, e.numero5].map((n, idx) => (
                              <span 
                                key={idx} 
                                className="numero-ball"
                                style={{
                                  background: numeroRicerca && parseInt(numeroRicerca) === n 
                                    ? 'linear-gradient(135deg, #f6e05e, #d69e2e)' 
                                    : undefined
                                }}
                              >
                                {n}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}

              {/* Paginazione */}
              {activeTab === 'recenti' && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                  <button 
                    className="btn btn-secondary"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                  >
                    ← Precedenti
                  </button>
                  <span style={{ padding: '10px', color: '#718096' }}>
                    Pagina {Math.floor(offset / limit) + 1}
                  </span>
                  <button 
                    className="btn btn-secondary"
                    disabled={estrazioni.length < limit}
                    onClick={() => setOffset(offset + limit)}
                  >
                    Successive →
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Estrazioni;
